import { useEffect, useRef } from 'react';
import stickyRegistry from '@buzzfeed/bf-utils/lib/sticky-registry';

const FLOATING_MODE_SELECTOR = '.cnx-float.cnx-pinned-top-floating';

// `observedRef` must be the player wrapper, not the render target: Connatix renders into a
// sibling node it inserts itself, leaving the render target empty and clipped by the wrapper's
// `overflow: hidden`, which stops IntersectionObserver from ever reporting it as intersecting.
export function useFloatingMode(playerApi, observedRef, enableFloating) {
  const isFloating = useRef(false);
  const floatingPlayerRef = useRef(null);
  const floatingSessionId = useRef(0);
  const floatingRafId = useRef(null);

  useEffect(() => {
    if (!enableFloating) {
      playerApi?.disableFloatingMode();
      if (floatingPlayerRef.current) {
        stickyRegistry.remove(floatingPlayerRef.current);
        floatingPlayerRef.current = null;
        isFloating.current = false;
      }
      return;
    }

    if (!enableFloating || !observedRef.current) return;

    if (typeof playerApi.enableFloatingMode !== 'function' || typeof playerApi.disableFloatingMode !== 'function') {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const playerElement = observedRef.current;

    const observerCallback = entries => {
      entries.forEach(entry => {
        const rect = entry.boundingClientRect;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        if (!entry.isIntersecting && rect.top < 0 && !isFloating.current) {
          playerApi.enableFloatingMode();
          isFloating.current = true;
          // Start a new floating session and schedule DOM updates for sticky behavior.
          floatingSessionId.current += 1;
          const sessionId = floatingSessionId.current;
          if (floatingRafId.current !== null) {
            cancelAnimationFrame(floatingRafId.current);
            floatingRafId.current = null;
          }
          floatingRafId.current = requestAnimationFrame(() => {
            floatingRafId.current = null;
            // Ensure floating is still enabled and this callback belongs to the current session.
            if (!isFloating.current || floatingSessionId.current !== sessionId) {
              return;
            }
            const floatingEl = document.querySelector(FLOATING_MODE_SELECTOR);
            if (floatingEl) {
              floatingPlayerRef.current = floatingEl;
              stickyRegistry.add(floatingEl, { priority: 'medium' });
            }
          });
        } else if (entry.isIntersecting && rect.bottom <= viewportHeight && isFloating.current) {
          playerApi.disableFloatingMode();
          isFloating.current = false;
          // Invalidate any pending floating session and cancel scheduled DOM updates.
          floatingSessionId.current += 1;
          if (floatingRafId.current !== null) {
            cancelAnimationFrame(floatingRafId.current);
            floatingRafId.current = null;
          }
          if (floatingPlayerRef.current) {
            stickyRegistry.remove(floatingPlayerRef.current);
            floatingPlayerRef.current = null;
          }
        }
      });
    };

    const observerOptions = { threshold: [0, 0.1], rootMargin: '0px' };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    observer.observe(playerElement);

    return () => {
      observer.unobserve(playerElement);
      observer.disconnect();

      // Cancel any pending floating-mode DOM updates.
      if (floatingRafId.current !== null) {
        cancelAnimationFrame(floatingRafId.current);
        floatingRafId.current = null;
      }
      floatingSessionId.current += 1;
      if (floatingPlayerRef.current) {
        stickyRegistry.remove(floatingPlayerRef.current);
        floatingPlayerRef.current = null;
      }
      isFloating.current = false;

    };
  }, [enableFloating, playerApi, observedRef]);
}
